import React, { useState, useEffect } from "react";
import { productAPI } from "../../Handlers/productAPI";
import { adminCategoryAPI } from "../../../Handlers/categoryAPI";
import styles from '../../styles/components/forms/editProductForm.module.css';

function EditProductForm({ product, onSuccess }) {
    // Estados para Arquivos (Imagens e Vídeo)
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(product.image_url);
    const [videoFile, setVideoFile] = useState(null);
    const [videoPreview, setVideoPreview] = useState(product.video_url);

    // Estados para Categorias
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);

    // Estados para Galeria Extra
    const [extraFiles, setExtraFiles] = useState({});
    const [extraPreviews, setExtraPreviews] = useState({
        image1: product.image1_url || null,
        image2: product.image2_url || null,
        image3: product.image3_url || null,
        image4: product.image4_url || null,
        image5: product.image5_url || null,
    });

    // Estado dos dados textuais e booleanos
    const [productData, setProductData] = useState({
        name: product.name || '',
        price: product.price || '',
        description: product.description || '',
        stock: product.stock || 0,
        featured: product.featured || false,
        options: product.options || '',
        is_active: product.is_active ?? true,
        category_id: product.category_id || '', // Campo crucial para o vínculo
        weight: product.weight || 0.500,
        width: product.width || 15,
        height: product.height || 15,
        length: product.length || 15
    });

    // Carrega categorias ao montar o componente
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await adminCategoryAPI.list();
                setCategories(response || []);
            } catch (error) {
                console.error("Erro ao carregar categorias:", error);
            }
        };
        fetchCategories();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setProductData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreview(URL.createObjectURL(selectedFile));
        }
    };

    const handleVideoChange = (e) => {
        const selectedVideo = e.target.files[0];
        if (selectedVideo) {
            setVideoFile(selectedVideo);
            setVideoPreview(URL.createObjectURL(selectedVideo));
        }
    };

    const handleExtraFileChange = (e, key) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setExtraFiles(prev => ({ ...prev, [key]: selectedFile }));
            setExtraPreviews(prev => ({ ...prev, [key]: URL.createObjectURL(selectedFile) }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const data = new FormData();
            
            // Adiciona campos de texto e booleanos
            Object.keys(productData).forEach(key => data.append(key, productData[key]));
            
            // Adiciona arquivos se houver novos selecionados
            if (file) data.append('image_file', file);
            if (videoFile) data.append('video_file', videoFile);
            
            Object.keys(extraFiles).forEach(key => {
                data.append(key, extraFiles[key]);
            });

            const response = await productAPI.update(product.id, data);
            onSuccess(response);
        } catch (error) {
            console.error(error);
            alert("Erro ao salvar alterações.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className={styles.formContainer}>
            {/* Header: Imagem Principal */}
            <div className={styles.imageHeader}>
                <div className={styles.imageWrapper}>
                    <img src={preview || '/placeholder-image.png'} alt="Preview" className={styles.profilePreview} />
                    <label htmlFor="image-upload" className={styles.changeImageBtn}>
                        📷 Alterar Foto Principal
                    </label>
                    <input id="image-upload" type="file" hidden onChange={handleFileChange} accept="image/*" />
                </div>
            </div>

            {/* Galeria Extra */}
            <div className={styles.galleryContainer}>
                <label className={styles.sectionLabel}>Galeria de Fotos (Opcional)</label>
                <div className={styles.thumbGrid}>
                    {['image1', 'image2', 'image3', 'image4', 'image5'].map((key, index) => (
                        <div key={key} className={styles.thumbWrapper}>
                            <label htmlFor={`file-${key}`} className={styles.thumbLabel}>
                                {extraPreviews[key] ? (
                                    <img src={extraPreviews[key]} alt={`Preview ${index}`} className={styles.thumbImg} />
                                ) : (
                                    <span className={styles.plusIcon}>+</span>
                                )}
                            </label>
                            <input id={`file-${key}`} type="file" hidden onChange={(e) => handleExtraFileChange(e, key)} accept="image/*" />
                        </div>
                    ))}
                </div>
            </div>

            <div className={styles.formBody}>
                <div className={styles.inputGroup}>
                    <label>Nome do Produto</label>
                    <input className={styles.input} name="name" value={productData.name} onChange={handleChange} required />
                </div>

                {/* Select de Categoria */}
                <div className={styles.inputGroup}>
                    <label>Categoria</label>
                    <select 
                        className={styles.selectInput} 
                        name="category_id" 
                        value={productData.category_id} 
                        onChange={handleChange} 
                        required
                    >
                        <option value="">Selecione uma categoria...</option>
                        {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.row}>
                    <div className={styles.inputGroup}>
                        <label>Preço (R$)</label>
                        <input className={styles.input} name="price" type="number" step="0.01" value={productData.price} onChange={handleChange} required />
                    </div>
                    <div className={styles.inputGroup}>
                        <label>Estoque</label>
                        <input className={styles.input} name="stock" type="number" value={productData.stock} onChange={handleChange} required />
                    </div>
                </div>

                <div className={styles.inputGroup}>
                    <label>Descrição Detalhada</label>
                    <textarea className={styles.textarea} name="description" value={productData.description} onChange={handleChange} rows="3" />
                </div>

                {/* Vídeo */}
                <div className={styles.videoUploadContainer}>
                    <label className={styles.sectionLabel}>Vídeo do Produto</label>
                    <div className={styles.videoDropzone}>
                        {videoPreview ? (
                            <div className={styles.videoPreviewWrapper}>
                                <video src={videoPreview} className={styles.videoPreviewElement} controls />
                                <label htmlFor="video-upload" className={styles.videoEditBtn}>Trocar Vídeo</label>
                            </div>
                        ) : (
                            <label htmlFor="video-upload" className={styles.videoPlaceholder}>
                                🎥 Adicionar Vídeo (MP4)
                            </label>
                        )}
                        <input id="video-upload" type="file" hidden onChange={handleVideoChange} accept="video/mp4" />
                    </div>
                </div>

                {/* Status Box */}
                <div className={styles.statusBox}>
                    <label className={styles.checkboxLabel}>
                        <input type="checkbox" name="featured" checked={productData.featured} onChange={handleChange} />
                        Destaque na Home
                    </label>
                    <label className={`${styles.checkboxLabel} ${!productData.is_active ? styles.inactiveText : ''}`}>
                        <input type="checkbox" name="is_active" checked={productData.is_active} onChange={handleChange} />
                        {productData.is_active ? "✅ Ativo" : "❌ Inativo"}
                    </label>
                </div>

                <button type="submit" className={styles.saveButton} disabled={loading}>
                    {loading ? 'Salvando...' : 'Salvar Todas as Alterações'}
                </button>
            </div>
        </form>
    );
}

export default EditProductForm;